import React, { useState } from 'react';
import { Form, Input, Button } from 'antd';
import { useLazyQuery } from '@common/hooks';
import { Colors } from 'works/legacy/lib/style-helper';
import { Icon } from '@common/components';
import {
  WorksAuthInfoQuery,
  WorksAuthInfoQueryType,
  MembershipType,
  UserType,
} from '@identity/graphql';

function FormErrorMessage() {
  return (
    <span>
      No account was found with that email address, please try again or contact
      our Works Specialist at{' '}
      <a href="mailto:support@workshealth.com">support@workshealth.com</a>
    </span>
  );
}

export type SignInEmailFormRecordType = {
  email: string;
  memberships: MembershipType[];
  user: UserType;
};

type Props = {
  onFinish: (params: SignInEmailFormRecordType) => void;
};

export function SignInEmailForm({ onFinish }: Props) {
  const [email, setEmail] = useState('');
  const [getUser, { loading, error, data }] =
    useLazyQuery<WorksAuthInfoQueryType>(WorksAuthInfoQuery, {
      onCompleted: (data) => {
        if (data.worksAuthInfo.user) {
          onFinish({
            email,
            memberships: data.worksAuthInfo.memberships,
            user: data.worksAuthInfo.user,
          });
        }
      },
    });

  const onFormFinish = (values: { email: string }) => {
    getUser({ variables: { userEmail: values.email } });
    setEmail(values.email);
  };

  if (loading) return <div>Looking up user...</div>;
  if (error) return <div>There was an unexpected error.</div>;

  const hasUserError = data && !data.worksAuthInfo.user;

  return (
    <div className="sign-in-email-form">
      <Form initialValues={{ email }} onFinish={onFormFinish}>
        <Form.Item
          name="email"
          validateStatus={hasUserError ? 'error' : ''}
          help={hasUserError && <FormErrorMessage />}
          rules={[
            {
              required: true,
              message: 'Please input your email',
            },
            {
              type: 'email',
              message: 'Please enter a valid email address',
            },
          ]}
        >
          <Input
            prefix={<Icon icon="envelope" kind="light" />}
            placeholder="email address"
            size="large"
          />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            className="sign-in-form-button"
            size="large"
          >
            Continue
          </Button>
        </Form.Item>
      </Form>
      <style jsx>{`
        .sign-in-email-form {
          width: 100%;
        }

        .sign-in-email-form :global(.ant-input-prefix) {
          color: ${Colors.steelDarker};
        }
        .sign-in-email-form :global(input) {
          padding-left: 0.4rem;
        }

        .sign-in-email-form :global(.sign-in-form-button) {
          width: auto;
        }

        /* Ant removes the bottom margin when there's help text but we want to keep it */
        .sign-in-email-form :global(.ant-form-item-with-help) {
          margin-bottom: 24px;
        }
      `}</style>
    </div>
  );
}
